/**
 * Vendored, typed copy of the unified-app RBAC model.
 *
 * SOURCE OF TRUTH: docs/architecture/rbac-model.json (v1.0.0). That JSON lives
 * OUTSIDE apps/web/src and the web tsconfig only includes `src`, so we vendor it
 * here as a typed constant — the same pattern the codebase uses for the PII spec
 * (canonical JSON → vendored in-app copy, drift caught by a sync test). Keep this
 * in lock-step with the canonical JSON; `rbac-model.spec.ts` pins the role set,
 * stored strings, surfaces, and derived-authority unions so a drift FAILS loudly
 * rather than silently shipping a stale role list.
 *
 * The role-set editor is "role-primary": an admin grants a SET of ROLES; the
 * user's AUTHORITIES are the read-only UNION of every granted role's authorities
 * (default-DENY — no roles ⇒ no authority anywhere). Per-permission overrides are
 * an explicitly-deferred follow-on (the "Advanced" expander), NOT modelled here.
 */

export type SurfaceKey =
  | 'platform'
  | 'chat'
  | 'crm'
  | 'expense'
  | 'commissions';

export interface RbacSurface {
  key: SurfaceKey;
  label: string;
  description: string;
}

export interface RbacAuthority {
  key: string;
  label: string;
  surface: SurfaceKey;
  /**
   * True when the platform admin role deliberately does NOT confer this
   * authority (canonical `explicit_grant_only`). Holding it takes an explicit
   * grant of the role that carries it — no admin bypass anywhere in the UI
   * (nav-gating's EXPLICIT_GRANT_ONLY_NAV relies on this).
   */
  explicitGrantOnly?: boolean;
}

export interface RbacRole {
  /** Stable internal key (NOT the stored string). */
  key: string;
  /** EXACT string persisted in experts.user_roles.role — the PATCH payload uses THIS. */
  stored: string;
  surface: SurfaceKey;
  label: string;
  description: string;
  /** Authority keys this role confers. The user's authority set is the union of these across granted roles. */
  authorities: string[];
}

export const RBAC_VERSION = '1.5.0' as const;

export const RBAC_SURFACES: readonly RbacSurface[] = [
  {
    key: 'platform',
    label: 'Platform',
    description:
      'Cross-surface platform administration. The single "admin" role is the existing experts admin and is full-access everywhere.',
  },
  {
    key: 'chat',
    label: 'Chat',
    description:
      'Andi/Andy assistant chat surface. New, additive. Chat access today is gated by CIAM login; these roles let admins grant/scope chat explicitly under the unified model.',
  },
  {
    key: 'crm',
    label: 'Customer Management',
    description:
      'Customer-management surface. Roles + capabilities are reused verbatim from cm-api. Stored namespaced as "crm:<role>".',
  },
  {
    key: 'expense',
    label: 'Expense',
    description:
      'Expense ledger surface. Authorities mirror the labs Authority enum names. Enforcement reads the token "roles" claim today; store convergence is a tracked follow-on.',
  },
  {
    key: 'commissions',
    label: 'Commissions',
    description:
      'Commissions review. Explicit grant only: platform admins do not get it automatically — grant the reviewer role to anyone who needs it.',
  },
] as const;

export const RBAC_AUTHORITIES: readonly RbacAuthority[] = [
  {
    key: 'platform:admin',
    label: 'Full platform administration',
    surface: 'platform',
  },
  { key: 'manage:users', label: 'Manage users + roles', surface: 'platform' },

  { key: 'use:chat', label: 'Use the chat assistant', surface: 'chat' },
  { key: 'view:chat_history', label: 'View own chat history', surface: 'chat' },
  {
    key: 'manage:chat_config',
    label: 'Manage chat/assistant configuration',
    surface: 'chat',
  },

  { key: 'view:home', label: 'View Customer Management home', surface: 'crm' },
  { key: 'view:customers', label: 'View customers list', surface: 'crm' },
  {
    key: 'view:customer_profile',
    label: 'View customer profile',
    surface: 'crm',
  },
  { key: 'view:account_health', label: 'View account health', surface: 'crm' },
  { key: 'view:followups', label: 'View follow-ups', surface: 'crm' },
  {
    key: 'view:team_work_queue',
    label: 'View the team work queue',
    surface: 'crm',
  },
  { key: 'view:segments', label: 'View segments', surface: 'crm' },
  {
    key: 'write:segment',
    label: 'Create, edit and delete segments',
    surface: 'crm',
  },
  {
    key: 'view:supplier_workspace',
    label: 'View Hotel Supplier sourcing workspace',
    surface: 'crm',
  },
  {
    key: 'view:supplier_renewals',
    label: 'View Hotel Supplier renewals',
    surface: 'crm',
  },
  {
    key: 'view:supplier_rate_opportunities',
    label: 'View Hotel Supplier rate opportunities',
    surface: 'crm',
  },
  {
    key: 'view:supplier_tab',
    label: 'View Hotel Supplier tab',
    surface: 'crm',
  },
  {
    key: 'view:user_management',
    label: 'View user management',
    surface: 'crm',
  },
  {
    key: 'view:hubspot_integration',
    label: 'View HubSpot integration',
    surface: 'crm',
  },
  {
    key: 'manage:hubspot_integration',
    label: 'Manage HubSpot integration',
    surface: 'crm',
  },
  { key: 'view:ae_routing', label: 'View AE routing', surface: 'crm' },
  { key: 'manage:ae_routing', label: 'Manage AE routing', surface: 'crm' },
  { key: 'write:contact', label: 'Write contact', surface: 'crm' },
  { key: 'delete:contact', label: 'Delete contact', surface: 'crm' },
  { key: 'write:note', label: 'Write note', surface: 'crm' },
  { key: 'delete:any_note', label: 'Delete any note', surface: 'crm' },
  { key: 'write:tag', label: 'Write tag', surface: 'crm' },
  { key: 'write:followup', label: 'Write follow-up', surface: 'crm' },
  {
    key: 'write:supplier',
    label: 'Write Hotel Supplier record',
    surface: 'crm',
  },
  {
    key: 'edit:customer_settings',
    label: 'Update customers and contacts',
    surface: 'crm',
  },
  {
    key: 'delete:customer_record',
    label: 'Delete contracts, entities, partner agencies',
    surface: 'crm',
  },
  // CSM Staffing Calculator (plan D5) — on the `crm` surface deliberately.
  {
    key: 'view:staffing',
    label: 'View the CSM staffing calculator',
    surface: 'crm',
  },
  {
    key: 'manage:staffing',
    label: 'Change staffing config, roster, slots and pins',
    surface: 'crm',
  },
  {
    key: 'view:staffing_financials',
    label: 'See staffing cost / profit figures',
    surface: 'crm',
  },

  {
    key: 'AUTHORITY_EXPENSE_USER',
    label: 'Submit/read expense entries',
    surface: 'expense',
  },
  {
    key: 'AUTHORITY_EXPENSE_APPROVER',
    label: 'Approve out-of-pocket/mileage expenses',
    surface: 'expense',
  },

  // Explicit grant only (1.5.0): NOT in the admin role below, on purpose.
  {
    key: 'review:commissions',
    label: 'Review commissions',
    surface: 'commissions',
    explicitGrantOnly: true,
  },
] as const;

const CRM_ADMIN_AUTHORITIES = [
  'view:home',
  'view:customers',
  'view:customer_profile',
  'view:account_health',
  'view:followups',
  'view:team_work_queue',
  'view:segments',
  'write:segment',
  'view:supplier_workspace',
  'view:supplier_renewals',
  'view:supplier_rate_opportunities',
  'view:supplier_tab',
  'view:user_management',
  'view:hubspot_integration',
  'manage:hubspot_integration',
  'view:ae_routing',
  'manage:ae_routing',
  'write:contact',
  'delete:contact',
  'write:note',
  'delete:any_note',
  'write:tag',
  'write:followup',
  'write:supplier',
  'edit:customer_settings',
  'delete:customer_record',
  'view:staffing',
  'manage:staffing',
  'view:staffing_financials',
  'manage:users',
];

export const RBAC_ROLES: readonly RbacRole[] = [
  {
    key: 'admin',
    stored: 'admin',
    surface: 'platform',
    label: 'Platform Admin',
    description:
      'Existing experts admin. Full access across every surface except explicit-grant-only authorities (Commissions), which need their own role. Stored as the bare string "admin" (backward-compat).',
    authorities: [
      'platform:admin',
      'manage:users',
      'use:chat',
      'view:chat_history',
      'manage:chat_config',
      ...CRM_ADMIN_AUTHORITIES.filter((a) => a !== 'manage:users'),
      'AUTHORITY_EXPENSE_USER',
      'AUTHORITY_EXPENSE_APPROVER',
    ],
  },
  {
    key: 'chat_user',
    stored: 'chat:user',
    surface: 'chat',
    label: 'Chat User',
    description:
      'Can use the chat assistant and see own history. New additive role.',
    authorities: ['use:chat', 'view:chat_history'],
  },
  {
    key: 'crm_admin',
    stored: 'crm:admin',
    surface: 'crm',
    label: 'Admin',
    description:
      'Customer Management admin. Full access (all Customer Management capabilities).',
    authorities: CRM_ADMIN_AUTHORITIES,
  },
  {
    key: 'crm_manager',
    stored: 'crm:manager',
    surface: 'crm',
    label: 'Manager',
    description:
      'Full Customer Management access; can edit customer settings and delete any note.',
    authorities: [
      'view:home',
      'view:customers',
      'view:customer_profile',
      'view:account_health',
      'view:followups',
      'view:team_work_queue',
      'view:segments',
      'write:segment',
      'view:supplier_workspace',
      'view:supplier_renewals',
      'view:supplier_rate_opportunities',
      'view:supplier_tab',
      'view:hubspot_integration',
      'view:ae_routing',
      'manage:ae_routing',
      'write:contact',
      'delete:contact',
      'write:note',
      'delete:any_note',
      'write:tag',
      'write:followup',
      'write:supplier',
      'edit:customer_settings',
      'delete:customer_record',
      'view:staffing',
      'manage:staffing',
      'view:staffing_financials',
    ],
  },
  {
    key: 'crm_csm',
    stored: 'crm:csm',
    surface: 'crm',
    label: 'CSM / AE',
    description:
      'Full Customer Management read; can update customers and contacts and write notes/tags/follow-ups/Hotel-Supplier records. Cannot delete.',
    authorities: [
      'view:home',
      'view:customers',
      'view:customer_profile',
      'view:account_health',
      'view:followups',
      'view:segments',
      'write:segment',
      'view:supplier_workspace',
      'view:supplier_renewals',
      'view:supplier_tab',
      'write:contact',
      'write:note',
      'write:tag',
      'write:followup',
      'write:supplier',
      'edit:customer_settings',
    ],
  },
  {
    key: 'crm_btp_only',
    stored: 'crm:btp_only',
    surface: 'crm',
    label: 'Hotel Supplier Mgr',
    description:
      'Sees only the Hotel Supplier Management module, across all customers. Can update customers and contacts.',
    authorities: [
      'view:customers',
      'view:customer_profile',
      'view:supplier_workspace',
      'view:supplier_renewals',
      'view:supplier_rate_opportunities',
      'view:supplier_tab',
      'write:supplier',
      'write:contact',
      'write:note',
      'write:tag',
      'edit:customer_settings',
    ],
  },
  {
    key: 'crm_viewer',
    stored: 'crm:viewer',
    surface: 'crm',
    label: 'Read-only',
    description:
      'Read-only across the whole Customer Management module — the only role that cannot change anything.',
    authorities: [
      'view:home',
      'view:customers',
      'view:customer_profile',
      'view:account_health',
      'view:followups',
      'view:segments',
      'view:supplier_workspace',
      'view:supplier_renewals',
      'view:supplier_tab',
    ],
  },
  {
    key: 'expense_user',
    stored: 'expense:user',
    surface: 'expense',
    label: 'Expense User',
    description: 'Submit and read own expense entries.',
    authorities: ['AUTHORITY_EXPENSE_USER'],
  },
  {
    key: 'expense_approver',
    stored: 'expense:approver',
    surface: 'expense',
    label: 'Expense Approver',
    description: 'Approve out-of-pocket / mileage expenses, plus submit/read.',
    authorities: ['AUTHORITY_EXPENSE_USER', 'AUTHORITY_EXPENSE_APPROVER'],
  },
  {
    key: 'commissions_reviewer',
    stored: 'commissions:reviewer',
    surface: 'commissions',
    label: 'Commissions Reviewer',
    description:
      'Reviews commissions. Explicit grant only — the Platform Admin role does not include it, so grant it to admins who need it too.',
    authorities: ['review:commissions'],
  },
] as const;

// ── Lookups + derivation helpers ────────────────────────────────────────────

const ROLE_BY_STORED = new Map(RBAC_ROLES.map((r) => [r.stored, r]));
const AUTHORITY_BY_KEY = new Map(RBAC_AUTHORITIES.map((a) => [a.key, a]));

/** The roles for a given surface, in declaration order. */
export function rolesForSurface(surface: SurfaceKey): RbacRole[] {
  return RBAC_ROLES.filter((r) => r.surface === surface);
}

/** Resolve a stored-string set to the known role objects (unknown strings ignored — they still ride along in the payload). */
export function rolesFromStored(stored: readonly string[]): RbacRole[] {
  return stored
    .map((s) => ROLE_BY_STORED.get(s))
    .filter((r): r is RbacRole => r != null);
}

/**
 * The DERIVED authority set: the de-duplicated UNION of every selected role's
 * authorities, returned as full authority objects in canonical declaration
 * order. This is what the editor renders read-only — the union an admin is
 * granting, never an editable surface itself.
 */
export function deriveAuthorities(
  storedRoles: readonly string[],
): RbacAuthority[] {
  const keys = new Set<string>();
  for (const role of rolesFromStored(storedRoles)) {
    for (const a of role.authorities) keys.add(a);
  }
  // Canonical order: iterate the declared authority list, keep the granted ones.
  return RBAC_AUTHORITIES.filter((a) => keys.has(a.key));
}

/** Just the authority keys (handy for tests / quick membership checks). */
export function deriveAuthorityKeys(storedRoles: readonly string[]): string[] {
  return deriveAuthorities(storedRoles).map((a) => a.key);
}

/**
 * True for an authority the platform admin role deliberately does not confer
 * (canonical `explicit_grant_only`). Unknown keys are false.
 */
export function isExplicitGrantOnly(key: string): boolean {
  return AUTHORITY_BY_KEY.get(key)?.explicitGrantOnly === true;
}

export function authorityLabel(key: string): string {
  return AUTHORITY_BY_KEY.get(key)?.label ?? key;
}

/**
 * Backward-compat bridge for the legacy binary roster. The roster's AdminUser
 * still carries the derived `role: 'admin' | 'user'`. If the server has not yet
 * returned the full `roles[]` set for a user, fall back to the legacy field:
 * 'admin' ⇒ ['admin'], 'user' ⇒ [] (default-deny). Once the server returns
 * `roles`, that authoritative set wins.
 */
export function storedRolesFromAdminUser(user: {
  role?: string;
  roles?: string[] | null;
}): string[] {
  if (Array.isArray(user.roles)) return [...user.roles];
  return user.role === 'admin' ? ['admin'] : [];
}
